import React from "react";
import { useCountry } from "../contexts/CountryContext";

const S = ({ title, children }) => (
  <section style={{ marginBottom: '28px' }}>
    <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '10px' }}>{title}</h2>
    <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', lineHeight: 1.8 }}>{children}</div>
  </section>
);

const PrivacyPolicyPage = () => {
  const { country, countryCode } = useCountry();
  return (
  <div className="page" data-testid="privacy-policy-page" style={{ maxWidth: '800px', margin: '0 auto' }}>
    <div className="page-header">
      <h1>Privacy Policy</h1>
      <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Last updated: April 2026</p>
    </div>

    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '32px' }}>
      <p style={{ fontSize: '0.9375rem', color: 'var(--text-dark)', lineHeight: 1.7, marginBottom: '28px' }}>
        HandyHub is committed to protecting your privacy. This Privacy Policy explains how we collect, use, store, and share your information when you use our platform.
      </p>

      <S title="1. Information We Collect">
        <p><strong>Account Information:</strong> When you create an account, we collect your name, email address, phone number, and location details.</p>
        <p><strong>Profile Information:</strong> Providers may submit additional business details including business name, service areas, qualifications, and verification documents.</p>
        <p><strong>Usage Data:</strong> We automatically collect information about how you interact with the platform, including pages visited, features used, timestamps, and device/browser information.</p>
        <p><strong>Communications:</strong> Messages exchanged through the platform, quote requests, and responses are stored to facilitate service delivery and support.</p>
      </S>

      <S title="2. How We Use Your Information">
        <p>We use your information to:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Provide, operate, and improve the HandyHub platform</li>
          <li>Match customers with relevant service providers</li>
          <li>Process subscriptions and related transactions</li>
          <li>Send service-related communications and notifications</li>
          <li>Verify provider identity and business credentials</li>
          <li>Detect and prevent fraud, abuse, and misuse</li>
          <li>Analyse usage patterns to improve performance and user experience</li>
        </ul>
      </S>

      <S title="3. Cookies and Analytics">
        <p>We use cookies and similar technologies to:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Maintain your session</li>
          <li>Remember preferences</li>
          <li>Understand platform usage</li>
        </ul>
        <p>You can manage cookies through your browser settings. Disabling cookies may affect some functionality.</p>
      </S>

      <S title="4. Data Sharing">
        <p>We do not sell your personal information.</p>
        <p>We may share your information with:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li><strong>Customers and Providers:</strong> To facilitate quotes, communication, and service delivery</li>
          <li><strong>Payment processors:</strong> To securely process subscription payments</li>
          <li><strong>Service providers:</strong> Such as hosting, analytics, and communication tools that help operate the platform</li>
          <li><strong>Analytics services:</strong> Using aggregated or anonymised data</li>
          <li><strong>Legal authorities:</strong> Where required by law or to protect rights, safety, or prevent fraud</li>
        </ul>
      </S>

      <S title="5. International Data Transfers">
        <p>Your information may be processed or stored in locations outside your country of residence.</p>
        <p>By using HandyHub, you consent to the transfer of your information to jurisdictions that may have different data protection laws.</p>
      </S>

      <S title="6. Data Retention">
        <p>We retain your information for as long as necessary to:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Maintain your account</li>
          <li>Provide platform services</li>
          <li>Comply with legal obligations</li>
        </ul>
        <p>If you close your account, we will delete or anonymise your data within a reasonable timeframe, unless retention is required by law.</p>
      </S>

      <S title="7. Data Security">
        <p>We use industry-standard security measures including encryption, secure storage, and access controls to protect your data.</p>
        <p>However, no system is completely secure, and we cannot guarantee absolute security.</p>
      </S>

      <S title="8. Your Rights">
        <p>Depending on your location, you may have the right to:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Access your personal data</li>
          <li>Correct inaccurate information</li>
          <li>Request deletion of your data</li>
          <li>Restrict or object to certain processing</li>
          <li>Request a copy of your data in a portable format</li>
          <li>Withdraw consent for communications</li>
        </ul>
        <p>To exercise your rights, contact: <a href={`mailto:${country.supportEmail}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }} data-testid="privacy-rights-email">{country.supportEmail}</a></p>
      </S>

      <S title="9. Children's Privacy">
        <p>HandyHub is not intended for individuals under the age of 18.</p>
        <p>We do not knowingly collect personal information from children.</p>
      </S>

      <S title="10. Changes to This Policy">
        <p>We may update this Privacy Policy from time to time.</p>
        <p>Material changes will be communicated via email or within the platform. Continued use of HandyHub constitutes acceptance of the updated policy.</p>
      </S>

      {countryCode === 'AU' && (
        <S title="User-Uploaded Content">
          <p data-testid="privacy-uploaded-content">When you request a quote you may optionally upload photos, videos, voice recordings, audio files and PDF documents to help providers understand your job. This content is stored securely and is accessible only to you, the providers matched to your request, and authorised HandyHub administrators. Please upload only material relevant to your job and avoid identification documents, payment details or other sensitive personal information. You may remove attachments before submitting your request. After submission, if you need an attachment removed, please contact HandyHub Support.</p>
        </S>
      )}

      <S title="11. Contact">
        <p>For privacy-related enquiries, please contact: <a href={`mailto:${country.supportEmail}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }} data-testid="privacy-contact-email">{country.supportEmail}</a></p>
      </S>
    </div>
  </div>
  );
};

export default PrivacyPolicyPage;
